<template>
  <modal
    ref="addUser"
    :title="!edit ? 'Nový užívateľ' : 'Upraviť užívateľa'"
    full-height
    name="add-user"
    scrollable
    size="lg"
    type="from-right"
    @hidden="Object.assign($data, $options.data())"
    @ok="saveUser"
    @show="fetchData"
  >
    <template #modal-footer="{ ok, cancel }">
      <template v-if="!loading && form.role !== 'admin'">
        <b-button
          v-if="form.restrictions.length"
          size="sm"
          :disabled="loading || permLoading"
          :variant="copied ? 'outline-primary' : 'outline-secondary'"
          @click="copyPerm"
        >
          Skopírovať oprávnenia
        </b-button>
        <b-button
          v-if="$store.state.user.storedRestrictions.length"
          size="sm"
          :disabled="loading || permLoading"
          variant="outline-secondary"
          @click="pastePerm"
        >
          Vložiť oprávnenia
        </b-button>
      </template>
      <b-button @click="cancel()"> Zrušiť </b-button>
      <b-button
        :disabled="loading || permLoading"
        variant="primary"
        @click="ok()"
      >
        {{ !edit ? 'Pridať' : 'Upraviť' }}
      </b-button>
    </template>
    <b-overlay :blur="null" :show="loading" class="h-100" opacity="0">
      <transition mode="out-in" name="fadeShort">
        <div v-if="!loading" ref="scroll" v-bar>
          <div>
            <b-row>
              <b-col md="6">
                <h4 class="mb-3">Osobné údaje</h4>
                <b-form-group label="Meno" label-for="firstname">
                  <b-form-input
                    v-model="form.firstname"
                    v-validate="'required'"
                    :class="{ 'is-invalid': errors.has('firstname') }"
                    data-vv-as="Meno"
                    name="firstname"
                  />

                  <b-form-invalid-feedback>
                    {{ errors.first('firstname') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group label="Priezvisko" label-for="lastname">
                  <b-form-input
                    v-model="form.lastname"
                    v-validate="'required'"
                    :class="{ 'is-invalid': errors.has('lastname') }"
                    data-vv-as="Priezvisko"
                    name="lastname"
                  />

                  <b-form-invalid-feedback>
                    {{ errors.first('lastname') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group label="Email" label-for="email">
                  <b-form-input
                    v-model="form.email"
                    v-validate="'required|email'"
                    :class="{ 'is-invalid': errors.has('email') }"
                    data-vv-as="Email"
                    name="email"
                  />

                  <b-form-invalid-feedback>
                    {{ errors.first('email') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group label="Telefón" label-for="phone">
                  <b-form-input v-model="form.phone" name="phone" />
                </b-form-group>
                <b-form-group label="Spoločnosť" label-for="company">
                  <b-form-select
                    v-model="form.company_id"
                    :select-size="1"
                    name="company_id"
                  >
                    <option :value="null" selected>--Nedefinované--</option>
                    <option
                      v-for="company in selection.companies"
                      :key="company.id"
                      :value="company.id"
                    >
                      {{ company.name }}
                    </option>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <h4 class="mb-3">Adresa</h4>
                <b-form-group label="Ulica" label-for="street">
                  <b-form-input
                    v-model="form.street"
                    v-validate="'required'"
                    :class="{ 'is-invalid': errors.has('street') }"
                    data-vv-as="Ulica"
                    name="street"
                  />

                  <b-form-invalid-feedback>
                    {{ errors.first('street') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group label="Číslo" label-for="streetNumber">
                  <b-form-input
                    v-model="form.streetNumber"
                    v-validate="'required|numeric'"
                    :class="{ 'is-invalid': errors.has('streetNumber') }"
                    data-vv-as="Číslo"
                    name="streetNumber"
                  />

                  <b-form-invalid-feedback>
                    {{ errors.first('streetNumber') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group label="Súp. číslo" label-for="streetRegNumber">
                  <b-form-input
                    v-model="form.streetRegNumber"
                    name="streetRegNumber"
                  />
                </b-form-group>
                <b-form-group label="Mesto" label-for="city">
                  <b-form-input
                    v-model="form.city"
                    v-validate="'required'"
                    :class="{ 'is-invalid': errors.has('city') }"
                    data-vv-as="Mesto"
                    name="city"
                  />

                  <b-form-invalid-feedback>
                    {{ errors.first('city') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group label="PSČ" label-for="postCode">
                  <b-form-input v-model="form.postCode" name="postCode" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <div class="col-md-6">
                <h4 class="mb-3">Nastavenia</h4>
                <b-form-group v-if="!edit" label="Heslo" label-for="password">
                  <b-input-group
                    :class="{ 'is-invalid': errors.has('password') }"
                    class="input-group-labeled"
                  >
                    <b-form-input
                      v-model="form.password"
                      v-validate="'required'"
                      :class="{ 'is-invalid': errors.has('password') }"
                      :type="showPass ? 'text' : 'password'"
                      autocomplete="new-password"
                      data-vv-as="Heslo"
                      name="password"
                    />
                    <b-input-group-append>
                      <b-btn variant="link" @click="showHide">
                        <b-icon :icon="showPass ? 'eye' : 'eye-slash'" />
                      </b-btn>
                      <b-btn variant="secondary" @click="generatePass">
                        Generovať
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                  <b-form-invalid-feedback>
                    {{ errors.first('password') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group label="Rola" label-for="role">
                  <b-form-select
                    v-model="form.role"
                    :select-size="1"
                    name="role"
                  >
                    <option selected value="user">Užívateľ</option>
                    <option value="admin">Administrátor</option>
                  </b-form-select>
                </b-form-group>
                <div v-if="!edit" class="form-group mb-2">
                  <b-form-checkbox v-model="form.sendWelcome" name="welcome">
                    Poslať novému užívateľovi e-mailovú správu o jeho účte.
                  </b-form-checkbox>
                </div>
              </div>
            </b-row>
            <b-row>
              <div class="col-md-6">
                <h4 class="mb-3">Prístupové práva</h4>
                <b-form-group label="Moduly" label-for="modules">
                  <selectbox
                    v-model="form.hasPermissions"
                    :options="[
                      { id: 1, name: 'Prevádzka', value: 'view operation' },
                      {
                        id: 2,
                        name: 'Trhové ceny',
                        value: 'view market-prices',
                      },
                    ]"
                    track-by="value"
                    :searchable="false"
                    multiple
                  />
                </b-form-group>
              </div>
            </b-row>
            <div
              v-if="
                form.id && form.hasPermissions.includes('view market-prices')
              "
            >
              <h4 class="mb-3">Trhové ceny - Notifikácie</h4>
              <energy-market ref="energyMarket" :user-id="form.id" />
              <hr />
            </div>
            <div>
              <h4 class="mb-3">Oprávnenia</h4>
              <b-overlay :blur="null" :show="permLoading" no-center opacity="0">
                <transition mode="out-in" name="fadeShort">
                  <add-permissions
                    ref="permissionForm"
                    :restrictions.sync="form.restrictions"
                    :role="form.role"
                    @onInit="permLoading = true"
                    @onLoaded="permLoading = false"
                  />
                </transition>
              </b-overlay>
            </div>
          </div>
        </div>
      </transition>
    </b-overlay>
  </modal>
</template>
<script>
import Selectbox from '@/components/inputs/Selectbox'
import Modal from '@/components/Modal'
import EnergyMarket from './EnergyMarket'
import AddPermissions from './Permissions'

export default {
  components: {
    Selectbox,
    Modal,
    EnergyMarket,
    AddPermissions,
  },
  data() {
    return {
      loading: false,
      permLoading: false,
      copied: false,
      edit: false,
      selection: {
        companies: [],
      },
      showPass: !this.edit,
      form: {
        password: null,
        hasPermissions: [],
        restrictions: [],
        role: 'user',
      },
    }
  },
  methods: {
    fetchData() {
      this.loading = true
      this.axios
        .all([
          this.$api.get('companies/get_companies'),
          this.edit ? this.$api.get('users/' + this.form.id) : null,
        ])
        .then(
          this.axios.spread((companies, formdata) => {
            this.selection.companies = companies.data
            if (this.edit) {
              Object.assign(this.form, formdata.data)
              this.form.role = formdata.data.roles[0].name
            }
            this.loading = false
          }),
        )
    },
    saveUser(e) {
      e.preventDefault()
      this.$validator.validateAll().then(() => {
        if (!this.errors.any() && !this.loading) {
          this.$api
            .post(this.edit ? 'users/edit' : 'users/add', this.form)
            .then((res) => {
              this.$swal({
                type: 'success',
                position: 'top',
                showCloseButton: true,
                text: res.data.success,
                timer: 5000,
              })
              if (
                this.form.id &&
                this.form.hasPermissions.includes('view market-prices')
              ) {
                this.$refs.energyMarket.save()
              }
              this.$root.$emit('modal-hide', 'add-user')
              this.$root.$emit('reloadUserData')
            })
        } else {
          this.$swal({
            type: 'warning',
            position: 'top',
            showCloseButton: true,
            text: 'Formulár nieje správne vyplnený',
            timer: 5000,
          })
        }
      })
    },
    showHide() {
      this.showPass = !this.showPass
    },
    generatePass() {
      this.form.password = this.$helpers.generatePass()
    },
    copyPerm() {
      this.$store.dispatch('user/copyRestrictions', this.form.restrictions)
      this.copied = true
    },
    pastePerm() {
      this.form.restrictions = [...this.$store.state.user.storedRestrictions]
    },
  },
}
</script>
